<template>
  <div>
    <page-title>{{ PageTitle }}</page-title>

    <loading v-if="loading" />

    <div v-else>
      <tabs v-if="$route.meta.isAdmin">
        <tabs-item
          :key="adminTabs.indexOf(tab)"
          v-for="tab in adminTabs"
          :link="tab.to"
          >{{ tab.title }}</tabs-item
        >
      </tabs>
      <tabs v-else>
        <tabs-item
          :key="tabs.indexOf(tab)"
          v-for="tab in tabs"
          :link="tab.to"
          >{{ tab.title }}</tabs-item
        >
      </tabs>
      <section-block>
        <template slot="header">
          <filter-bar
            resource="News"
            :search-query="searchQuery"
            :show-search="true"
            :show-filter="true"
            :filter-options="categories"
          ></filter-bar>
        </template>

        <template slot="actions" v-if="$route.meta.isAdmin">
          <base-button
            :primary="true"
            @click="$router.push({ name: 'AdminNewsCreate' })"
            iconBefore="document-create"
            v-if="userCanAdd"
          >
            <span>Add an article</span>
          </base-button>
        </template>

        <vuetable
          class="c-data-table"
          :api-mode="false"
          :fields="table.fields"
          :data-manager="dataManager"
          pagination-path
          ref="newsTable"
          @vuetable:pagination-data="onPaginationData"
          @vuetable:row-clicked="onRowClicked"
        >
          <template slot="date-created" slot-scope="props">
            <base-datetime :date="props.rowData.created_time"></base-datetime>
          </template>

          <template slot="categories" slot-scope="props">
            <p>{{ categoryList(props.rowData.categories) }}</p>
          </template>

          <template slot="category-actions" slot-scope="props">
            <news-actions
              routePath="AdminNewsEdit"
              :id="props.rowData.id"
              deleteAction="News/delete"
              :rowData="props.rowData"
              @tableUpdate="updateTable"
            />
          </template>
        </vuetable>
        <pagination table-pagination>
          <vuetable-pagination
            ref="pagination"
            @vuetable-pagination:change-page="onChangePage"
          ></vuetable-pagination>
        </pagination>
      </section-block>
    </div>
  </div>
</template>

<script>
import NewsActions from "@/components/actions/NewsActions";
import Pagination from "@/components/pagination";
import VuetablePagination from "vuetable-2/src/components/VuetablePagination";
import UserHasPermission from "@/mixins/UserHasPermission";
import querySearch from "@/mixins/querySearch";
import { mapState } from "vuex";
export default {
  components: {
    NewsActions,
    Pagination,
    VuetablePagination,
  },
  mixins: [UserHasPermission, querySearch],
  data() {
    return {
      PageTitle: "News",
      loading: true,
      searchQuery: "",
      table: {
        fields: [
          {
            name: "title",
            titleClass: "u-ta-left",
            title: "Article Name",
            width: "30%",
          },
          {
            name: "status",
            titleClass: "u-ta-left",
            title: "Status",
            width: "20%",
          },
          {
            name: "categories",
            titleClass: "u-ta-left",
            title: "Categories",
            width: "20%",
          },
          {
            name: "date-created",
            titleClass: "u-ta-left",
            title: "Date Created",
            width: "20%",
          },
          {
            name: "category-actions",
            title: "Actions",
            titleClass: "u-ta-center u-hide-mobile",
            dataClass: "u-ta-center u-hide-mobile",
            width: "10%",
          },
        ],
      },
    };
  },
  created() {
    this.$store.commit("News/SET_PUBLISHED_FILTER", false);
    this.$store.commit("News/SET_TRUST_FILTER", false);
    this.$store.commit("News/SET_RESULT_COUNT", 10);
    if (this.$route.params.tabFilter) {
      this.$store.commit("News/setTabFilter", [this.$route.params.tabFilter]);
    } else {
      this.$store.commit("News/setTabFilter", ["approved", "published"]);
    }
    this.setFiltersFromQuery("News");
    this.$store
      .dispatch("News/list", { includeWeb: false, page: 1 })
      .then((resp) => {
        this.loading = false;
      });
    this.$store.dispatch("NewsCategories/list");
  },
  watch: {
    // refresh table when filtering
    articles(newValue, oldValue) {
      if (this.$refs.newsTable) {
        this.$refs.newsTable.refresh();
      }
    },
  },
  methods: {
    updateTable() {
      this.$refs.newsTable.refresh();
    },
    onRowClicked(e) {
      if (
        e.event.target.localName === "td" ||
        e.event.target.parentNode.localName === "td"
      ) {
        this.$router.push({
          name: "AdminNewsEdit",
          params: { id: e.data.id },
        });
      }
    },
    categoryList(cats) {
      if (!cats) {
        return "Not categorised";
      }

      const categories = cats.map((item) => item.name);

      return categories.join(", ");
    },
    dataManager(sortOrder, pagination) {
      const returnObj = {
        ...this.$store.state.News.pagination,
        data: this.articles,
      };

      return returnObj;
    },
    onPaginationData(paginationData) {
      paginationData.current_page = this.getCurrentPageNumber('News');
      this.$refs.pagination.setPaginationData(paginationData);
    },
    onChangePage(page) {
      const vm = this;
      const tableref = vm.$refs.newsTable;
      let nextPage;
      this.loading = true;

      switch (page) {
        case "next":
          nextPage = ++this.$store.state.News.pagination.current_page;
          break;
        case "prev":
          nextPage = --this.$store.state.News.pagination.current_page;
          break;
        default:
          nextPage = page;
          break;
      }
      this.updatePageQuery('News', nextPage)
      this.$store.dispatch("News/list", { includeWeb: false }).then((resp) => {
        window.scroll(0, 0);
        tableref.changePage(nextPage);
        this.loading = false;
      });
    },
  },
  computed: {
    ...mapState("News", ["articles"]),
    ...mapState("NewsCategories", ["categories"]),
    userCanAdd() {
      return this.userHasPermission("add_edit_news");
    },
    adminTabs() {
      const vm = this;
      return [
        {
          to: `/admin/news`,
          title: `Approved`,
        },
        {
          to: `/admin/news/pending`,
          title: "Awaiting Approval",
        },
        {
          to: `/admin/news/rejected`,
          title: `Rejected`,
        },
      ];
    },
  },
};
</script>

<style scoped>
/**
 * TODO: Figure out why vuetable is doing a td within a td
 **/
td > td {
  border: 0;
  margin: 0;
  padding: 0;
  display: block;
}
</style>
